// A board someone shared with you (Features 1, sharing): it opens as they have it, live. Can edit: the board itself,
// to change its widgets, pages, tasks, posts and deals. Can view: as a shared link draws it. A bar along the top says
// whose it is and what you can do, with the way back to your own boards. Nothing of the owner's account (their
// chat, keys, connections, other boards, settings) is here: the board's pass opens this board alone.

import { Suspense } from "react";
import { ArrowLeft, Eye, PencilLine, Users } from "lucide-react";
import { kindOf } from "../../shared/boardKinds";
import type { Board } from "../../shared/types";
import { Canvas } from "../canvas/Canvas";
import { cx } from "../lib/util";
import { getToken } from "../lib/api";
import { useStore } from "../state/store";
import { leaveShared } from "../state/guest";
import { ViewFullscreenButton } from "../chrome/Fullscreen";
import { KINDS } from "./kinds";

/** The bar along the top: the board, whose it is, Can edit or Can view, and back to your own boards. */
export function GuestBar({ phone = false }: { phone?: boolean }) {
  const g = useStore((s) => s.guest);
  const board = useStore((s) => s.boards[0]);
  if (!g || !board) return null;
  const edit = g.role === "edit";
  return (
    <div className={cx("guest-bar island", phone && "is-phone")} role="status">
      <a className="guest-bar-back" href="/" aria-label={getToken() ? "Back to your boards" : "Boards shared with you"} data-tip={getToken() ? "Your boards" : "Shared with you"}>
        <ArrowLeft size={14} />
      </a>
      <span className="guest-bar-name">
        {board.emoji ? <span aria-hidden>{board.emoji} </span> : null}
        {board.name}
      </span>
      <span className="guest-bar-dot" aria-hidden>
        ·
      </span>
      <span className="guest-bar-owner">
        <Users size={13} aria-hidden /> Shared by {g.owner}
      </span>
      <button
        className="guest-bar-leave"
        onClick={() => {
          if (confirm(`Leave “${board.name}”? It goes from your list, and ${g.owner} can share it with you again.`)) void leaveShared().catch((e: Error) => alert(e.message));
        }}
      >
        Leave
      </button>
      {!phone && <ViewFullscreenButton board={board} edit={edit} />}
      <span className={cx("guest-bar-role", edit && "is-edit")} title={edit ? "You can change this board's widgets, pages, tasks, posts and deals." : "You can see this board. Its owner can make you an editor."}>
        {edit ? <PencilLine size={12} aria-hidden /> : <Eye size={12} aria-hidden />} {edit ? "Can edit" : "Can view"}
      </span>
    </div>
  );
}

/** A shared board you can only view: as a shared link draws it. */
export function GuestViewBoard({ board }: { board: Board }) {
  const kind = kindOf(board);
  const Shared = KINDS[kind]?.Shared;
  if (kind === "canvas") return <Canvas readOnly />;
  return (
    <div className="shell is-public is-guest" data-kind={kind}>
      {Shared ? (
        <Suspense fallback={null}>
          <Shared board={board} />
        </Suspense>
      ) : (
        <section className="shell-surface shell-not-here">
          <p>This kind of board does not open for viewing yet. Ask its owner to make you an editor.</p>
        </section>
      )}
    </div>
  );
}
